import React, { useEffect, useRef, useState } from "react";
import { ChatClient, ChatMessage } from "../net/chatClient";

export interface ChatOverlayProps {
  client: ChatClient | null;
  selfName: string;
}

interface DisplayMsg {
  id: string;
  user: string;
  text: string;
  self: boolean;
  ts: number;
}

export const ChatOverlay: React.FC<ChatOverlayProps> = ({ client, selfName }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<DisplayMsg[]>([]);
  const [draft, setDraft] = useState("");
  const [unread, setUnread] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!client) return;
    const off = client.onMessage((m: ChatMessage) => {
      setMsgs((prev) => [
        ...prev,
        {
          id: `r-${m.id}-${m.createdAt}`,
          user: m.user || "Player",
          text: m.messageData,
          self: false,
          ts: m.createdAt,
        },
      ]);
      setUnread((u) => (open ? 0 : u + 1));
    });
    return off;
  }, [client, open]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, open]);

  if (!client) return null;

  const sendDraft = () => {
    const text = draft.trim();
    if (!text) return;
    client.send(text);
    setMsgs((prev) => [
      ...prev,
      { id: `s-${Date.now()}`, user: selfName, text, self: true, ts: Date.now() },
    ]);
    setDraft("");
  };

  return (
    <>
      <button
        aria-label="Open chat"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) setUnread(0);
        }}
        style={{
          position: "absolute",
          right: 12,
          bottom: 12,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: open ? "#FED100" : "#268529",
          color: open ? "#0d3a1b" : "#fff",
          border: "none",
          fontSize: 22,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
          zIndex: 50,
        }}
      >
        {open ? "×" : "💬"}
        {!open && unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              background: "#d52727",
              color: "#fff",
              borderRadius: 999,
              fontSize: 11,
              padding: "2px 6px",
              minWidth: 18,
            }}
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            right: 12,
            bottom: 76,
            width: "min(320px, calc(100% - 24px))",
            height: "min(420px, calc(100% - 100px))",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
            zIndex: 50,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              background: "#0d3a1b",
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Game chat
          </div>
          <div
            ref={listRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              background: "#f6f6f6",
            }}
          >
            {msgs.length === 0 && (
              <div style={{ color: "#888", fontSize: 12, textAlign: "center", marginTop: 24 }}>
                No messages yet. Say hi!
              </div>
            )}
            {msgs.map((m) => (
              <div
                key={m.id}
                style={{
                  alignSelf: m.self ? "flex-end" : "flex-start",
                  maxWidth: "82%",
                  background: m.self ? "#268529" : "#e8e8e8",
                  color: m.self ? "#fff" : "#1f1f1f",
                  padding: "6px 10px",
                  borderRadius: 12,
                  fontSize: 13,
                  lineHeight: 1.3,
                }}
              >
                {!m.self && (
                  <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 2 }}>
                    {m.user}
                  </div>
                )}
                {m.text}
              </div>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendDraft();
            }}
            style={{
              display: "flex",
              gap: 6,
              padding: 8,
              borderTop: "1px solid #ddd",
              background: "#fff",
            }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Message"
              style={{
                flex: 1,
                padding: "8px 10px",
                borderRadius: 999,
                border: "1px solid #ccc",
                fontSize: 13,
                outline: "none",
              }}
            />
            <button
              type="submit"
              style={{
                padding: "8px 14px",
                background: "#268529",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
};
